import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Edit3, Trash2, Copy, Users, Calendar, MapPin, Clock, Globe, Lock, Star } from 'lucide-react';
import { useProfessionalTemplates } from '@/hooks/useProfessionalTemplates';
import { useClients } from '@/hooks/useClients';
import { ProfessionalEventTemplate, RSVPQuestion } from '@/services/professionalTemplateService';
import { RSVPQuestionBuilder } from '@/components/RSVPQuestionBuilder';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const PROFESSIONAL_CATEGORIES = [
  'Corporate Events',
  'Conferences & Meetings',
  'Product Launches',
  'Networking Events',
  'Team Building',
  'Training & Workshops',
  'Client Entertainment',
  'Award Ceremonies',
  'Trade Shows',
  'Fundraising Events'
];

interface TemplateFormData {
  name: string;
  description: string;
  category: string;
  event_type: string;
  client_id?: string;
  default_options: Array<{ type: string; values: string[] }>;
  default_duration_hours: number;
  default_reminder_days: number;
  is_public: boolean;
  industry_rsvp_questions: RSVPQuestion[];
  template_design_config: any;
}

export const ProfessionalTemplateManager: React.FC = () => {
  const navigate = useNavigate();
  const { templates, publicTemplates, loading, createTemplate, updateTemplate, deleteTemplate, useTemplate } = useProfessionalTemplates();
  const { clients } = useClients();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ProfessionalEventTemplate | null>(null);
  const [formData, setFormData] = useState<TemplateFormData>({
    name: '',
    description: '',
    category: '',
    event_type: 'voting',
    default_options: [
      { type: 'date', values: [] },
      { type: 'time', values: [] },
      { type: 'location', values: [] }
    ],
    default_duration_hours: 2,
    default_reminder_days: 7,
    is_public: false,
    industry_rsvp_questions: [],
    template_design_config: { theme: 'professional', primaryColor: '#007bff', layout: 'standard' }
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: '',
      event_type: 'voting',
      default_options: [
        { type: 'date', values: [] },
        { type: 'time', values: [] },
        { type: 'location', values: [] }
      ],
      default_duration_hours: 2,
      default_reminder_days: 7,
      is_public: false,
      industry_rsvp_questions: [],
      template_design_config: { theme: 'professional', primaryColor: '#007bff', layout: 'standard' }
    });
    setEditingTemplate(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingTemplate) {
        await updateTemplate(editingTemplate.id, formData);
        toast.success('Template updated successfully');
      } else {
        await createTemplate(formData);
        toast.success('Template created successfully');
      }
      
      setIsCreateDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    }
  };

  const handleEdit = (template: ProfessionalEventTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      category: template.category,
      event_type: template.event_type,
      client_id: template.client_id || undefined,
      default_options: template.default_options,
      default_duration_hours: template.default_duration_hours || 2,
      default_reminder_days: template.default_reminder_days || 7,
      is_public: template.is_public,
      industry_rsvp_questions: template.industry_rsvp_questions || [],
      template_design_config: template.template_design_config || { theme: 'professional', primaryColor: '#007bff', layout: 'standard' }
    });
    setIsCreateDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await deleteTemplate(id);
      toast.success('Template deleted successfully');
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const handleUseTemplate = async (template: ProfessionalEventTemplate) => {
    try {
      await useTemplate(template);
      navigate('/create-event', { 
        state: { 
          template: {
            name: template.name,
            description: template.description,
            category: template.category,
            options: template.default_options,
            eventType: template.event_type,
            clientId: template.client_id
          }
        }
      });
    } catch (error) {
      console.error('Error using template:', error);
      toast.error('Failed to use template');
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Corporate Events': return <Users className="w-4 h-4" />;
      case 'Conferences & Meetings': return <Calendar className="w-4 h-4" />;
      case 'Product Launches': return <Star className="w-4 h-4" />;
      default: return <Calendar className="w-4 h-4" />;
    }
  };

  const TemplateCard = ({ template, showEditControls = true }: { template: ProfessionalEventTemplate; showEditControls?: boolean }) => (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            {getCategoryIcon(template.category)}
            <div>
              <CardTitle className="text-lg">{template.name}</CardTitle>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="secondary" className="text-xs">
                  {template.category}
                </Badge>
                {template.is_public ? (
                  <Badge variant="outline" className="text-xs">
                    <Globe className="w-3 h-3 mr-1" />
                    Public
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs">
                    <Lock className="w-3 h-3 mr-1" />
                    Private
                  </Badge>
                )}
              </div>
            </div>
          </div>
          {showEditControls && (
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={() => handleEdit(template)}>
                <Edit3 className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(template.id)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <CardDescription className="mb-3">
          {template.description || 'No description provided'}
        </CardDescription>
        
        <div className="space-y-2 text-sm text-muted-foreground mb-4">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Duration: {template.default_duration_hours}h | Reminder: {template.default_reminder_days} days
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {template.event_type.toUpperCase()}
            </Badge>
            <span>•</span>
            <span>Used {template.usage_count} times</span>
            {template.industry_rsvp_questions && template.industry_rsvp_questions.length > 0 && (
              <>
                <span>•</span>
                <span>{template.industry_rsvp_questions.length} custom questions</span>
              </>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          <Button 
            onClick={() => handleUseTemplate(template)}
            className="flex-1"
          >
            <Copy className="w-4 h-4 mr-2" />
            Use Template
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Professional Event Templates</h2>
          <p className="text-muted-foreground">Create and manage templates for your professional events</p>
        </div>
        
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => resetForm()}>
              <Plus className="w-4 h-4 mr-2" />
              New Template
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingTemplate ? 'Edit Template' : 'Create New Template'}
              </DialogTitle>
              <DialogDescription>
                Create a reusable template for your professional events
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Template Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Annual Company Meeting"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="category">Category *</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROFESSIONAL_CATEGORIES.map((category) => (
                        <SelectItem key={category} value={category}>
                          {category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe this template..."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="event_type">Event Type</Label>
                  <Select 
                    value={formData.event_type} 
                    onValueChange={(value: string) => setFormData(prev => ({ ...prev, event_type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="voting">Voting</SelectItem>
                      <SelectItem value="rsvp">RSVP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="duration">Duration (hours)</Label>
                  <Input
                    id="duration"
                    type="number"
                    min="0.5"
                    step="0.5"
                    value={formData.default_duration_hours}
                    onChange={(e) => setFormData(prev => ({ ...prev, default_duration_hours: parseFloat(e.target.value) }))}
                  />
                </div>
                
                <div>
                  <Label htmlFor="reminder">Reminder (days)</Label>
                  <Input
                    id="reminder"
                    type="number"
                    min="1"
                    value={formData.default_reminder_days}
                    onChange={(e) => setFormData(prev => ({ ...prev, default_reminder_days: parseInt(e.target.value) }))}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="client">Associated Client (Optional)</Label>
                <Select 
                  value={formData.client_id || ''} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, client_id: value || undefined }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select client (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific client</SelectItem>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Industry-Specific RSVP Questions */}
              <div className="space-y-4">
                <Label>Industry-Specific RSVP Questions</Label>
                <div className="space-y-2">
                  {formData.industry_rsvp_questions?.map((question, index) => (
                    <div key={index} className="flex items-center gap-2 p-3 border rounded">
                      <span className="flex-1 text-sm">{question.question}</span>
                      <Badge variant="outline" className="text-xs">
                        {question.type}
                      </Badge>
                      {question.required && (
                        <Badge variant="secondary" className="text-xs">Required</Badge>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newQuestions = formData.industry_rsvp_questions?.filter((_, i) => i !== index) || [];
                          setFormData(prev => ({ ...prev, industry_rsvp_questions: newQuestions }));
                        }}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newQuestion: RSVPQuestion = { 
                        id: `question_${Date.now()}`,
                        question: 'Dietary restrictions?', 
                        type: 'text', 
                        required: false,
                        category: 'dining'
                      };
                      setFormData(prev => ({ 
                        ...prev, 
                        industry_rsvp_questions: [...(prev.industry_rsvp_questions || []), newQuestion]
                      }));
                    }}
                  >
                    <Plus className="w-3 h-3 mr-2" />
                    Add RSVP Question
                  </Button>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="public"
                  checked={formData.is_public}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_public: checked }))}
                />
                <Label htmlFor="public">Make this template public for others to use</Label>
              </div>

              <RSVPQuestionBuilder
                questions={formData.industry_rsvp_questions}
                onQuestionsChange={(questions) => setFormData(prev => ({ ...prev, industry_rsvp_questions: questions }))}
              />

              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1">
                  {editingTemplate ? 'Update Template' : 'Create Template'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsCreateDialogOpen(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="my-templates" className="space-y-4">
        <TabsList>
          <TabsTrigger value="my-templates">My Templates ({templates.length})</TabsTrigger>
          <TabsTrigger value="public-templates">Public Templates ({publicTemplates.length})</TabsTrigger>
        </TabsList>
        
        <TabsContent value="my-templates" className="space-y-4">
          {templates.length === 0 ? (
            <Card>
              <CardContent className="text-center py-8">
                <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold mb-2">No templates yet</h3>
                <p className="text-muted-foreground mb-4">
                  Create your first professional event template to streamline your event planning
                </p>
                <Button onClick={() => setIsCreateDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Template
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => (
                <TemplateCard key={template.id} template={template} />
              ))}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="public-templates" className="space-y-4">
          {publicTemplates.length === 0 ? (
            <Card>
              <CardContent className="text-center py-8">
                <Globe className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold mb-2">No public templates available</h3>
                <p className="text-muted-foreground">
                  Check back later for community-shared templates
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {publicTemplates.map((template) => (
                <TemplateCard key={template.id} template={template} showEditControls={false} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};